// Considered: SphereSubscriptionsMolecule — rejected because existing molecule renders the
// legacy list/popover shape without per-row ScopeSegment, ScopeCoin avatar from cache, or
// the two-line stacked layout. Refactoring into this shape is the spec intent. See spec
// Reuse Audit (specs/sphere-selector-and-subscriptions.md).
import React, { useCallback } from 'react';
import { useTranslation } from 'localization';
import ScopeCoinAtom, { ScopeCoinScope } from '../../atoms/marketplace/ScopeCoinAtom';
import ScopeSegmentAtom from '../../atoms/ScopeSegment/ScopeSegmentAtom';
import { useSphereInfo } from '../../../hooks/useSphereInfo';
import type { ScopeMode } from 'types/channel';
import './SphereSubscriptionRowMolecule.scss';

// Maps sphere schema type → ScopeCoin gradient scope.
const TYPE_SCOPE: Record<string, ScopeCoinScope> = {
  organization: 'organization',
  department:   'center',
  person:       'all',
  role:         'center',
  project:      'metasphere',
  initiative:   'metasphere',
  approach:     'metasphere',
  period:       'all',
};

function typeToScope(type: string | undefined): ScopeCoinScope {
  return TYPE_SCOPE[type ?? ''] ?? 'all';
}

export interface SphereSubscriptionRowMoleculeProps {
  subscriptionId: string;
  sphereId: string;
  scopeMode: ScopeMode;
  canManage: boolean;
  /** Optional parent path for secondary line. Omitted gracefully when absent. */
  parentPath?: string[];
  onRemove: (subscriptionId: string, sphereId: string) => void;
  onScopeChange: (subscriptionId: string, sphereId: string, scopeMode: ScopeMode) => void;
  isRemoving?: boolean;
}

const SphereSubscriptionRowMolecule: React.FC<SphereSubscriptionRowMoleculeProps> = ({
  subscriptionId,
  sphereId,
  scopeMode,
  canManage,
  parentPath,
  onRemove,
  onScopeChange,
  isRemoving = false,
}) => {
  const { t } = useTranslation();
  const { info } = useSphereInfo(sphereId);

  const sphereName =
    info?.label ||
    info?.properties?.name ||
    t('sphereChannels.subscriptionsPanel.row.deletedSphere');

  const scope = typeToScope(undefined); // sphere type not available from subscription; fall back to 'all'
  const glyph = info?.properties?.glyph;

  const handleRemove = useCallback(() => {
    onRemove(subscriptionId, sphereId);
  }, [subscriptionId, sphereId, onRemove]);

  const handleScopeChange = useCallback((value: ScopeMode) => {
    onScopeChange(subscriptionId, sphereId, value);
  }, [subscriptionId, sphereId, onScopeChange]);

  const pathParts = parentPath ?? [];
  const pathLabel = pathParts.length > 0
    ? pathParts.slice(-2).join(' › ')
    : undefined;

  return (
    <div className="sphere-subscription-row">
      {/* Top line: coin + name/path + remove */}
      <div className="sphere-subscription-row__top">
        <ScopeCoinAtom
          scope={scope}
          glyph={glyph ?? undefined}
          channelName={sphereName}
          size="sm"
          className="sphere-subscription-row__coin"
        />
        <div className="sphere-subscription-row__info">
          <span className="sphere-subscription-row__name">{sphereName}</span>
          {pathLabel && (
            <span className="sphere-subscription-row__path">{pathLabel}</span>
          )}
        </div>
        {canManage && (
          <button
            type="button"
            className="sphere-subscription-row__remove"
            onClick={handleRemove}
            disabled={isRemoving}
            aria-label={t('sphereChannels.subscriptionsPanel.row.removeAriaLabel', { name: sphereName })}
          >
            {isRemoving ? (
              <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            ) : (
              <i className="pi pi-times" aria-hidden="true" />
            )}
          </button>
        )}
      </div>

      {/* Bottom line: scope segment full-width */}
      {canManage && (
        <div className="sphere-subscription-row__bottom">
          <ScopeSegmentAtom
            value={scopeMode}
            onChange={handleScopeChange}
            ariaLabel={t('sphereChannels.subscriptionsPanel.scopeSegment.ariaLabel')}
          />
        </div>
      )}
    </div>
  );
};

export default SphereSubscriptionRowMolecule;
